import { collection, doc, getDoc, getDocs, query, where, limit } from 'firebase/firestore';
import { db } from '../firebase';
import { PAGING } from '../config/paging';
import { backendConfig } from '../config/backendConfig';

const GENERIC_ARTIST_LABELS = new Set(['artist', 'young artist', 'anonymous', 'unknown artist']);

function normalizeCompareText(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/** Skip auto-generated book upload titles that repeat the activity header. */
function pickPeerArtworkTitle(data = {}, descriptor = null) {
  const raw = (data.title || data.artworkTitle || '').trim();
  const technique = (data.technique || '').trim();
  const inspiration = (data.inspiration || '').trim();

  if (!raw) return technique || inspiration || '';

  if (descriptor?.type === 'book') {
    const book = normalizeCompareText(descriptor.book);
    const code = normalizeCompareText(descriptor.activityCode);
    const titleNorm = normalizeCompareText(raw);
    const isGenericBookTitle =
      (titleNorm.includes('artwork from') || titleNorm.startsWith(book)) &&
      titleNorm.includes(book) &&
      titleNorm.includes(code);
    if (isGenericBookTitle) return technique || inspiration || '';
  }

  if (descriptor?.title) {
    const activityNorm = normalizeCompareText(descriptor.title);
    const titleNorm = normalizeCompareText(raw);
    if (
      titleNorm === activityNorm ||
      titleNorm.includes(activityNorm) ||
      activityNorm.includes(titleNorm)
    ) {
      return technique || inspiration || '';
    }
  }

  return raw;
}

function pickPeerArtistName(data = {}) {
  const fields = [
    data.artistName,
    data.userName,
    data.displayName,
    data.studentName,
    data.nickname,
    data.name,
  ];
  for (const field of fields) {
    if (typeof field === 'string' && field.trim()) return field.trim();
  }
  const email = data.email || data.userEmail;
  if (typeof email === 'string' && email.includes('@')) {
    const local = email.split('@')[0].trim();
    if (local) return local;
  }
  return '';
}

function isGenericArtistLabel(name) {
  return GENERIC_ARTIST_LABELS.has(String(name || '').trim().toLowerCase());
}

/** Same consent-aware resolution used by Gallery Studio / public gallery pages. */
async function resolveArtistDisplayName(uid) {
  if (!uid) return '';

  try {
    const profileSnap = await getDocs(
      query(collection(db, 'artistProfiles'), where('uid', '==', uid))
    );
    const profile = profileSnap.empty ? null : profileSnap.docs[0].data();

    let displayName = profile?.name || profile?.nickname || '';

    try {
      const response = await fetch(`${backendConfig.baseUrl}/api/consent/public-display/${uid}`);
      if (response.ok) {
        const data = await response.json();
        const canDisplayName = data.canDisplayName !== false;
        if (profile) {
          displayName = canDisplayName
            ? profile.name || profile.nickname || ''
            : profile.nickname || profile.name || '';
        }
      }
    } catch (consentErr) {
      console.warn('[museumActivityInsights] consent lookup failed:', uid, consentErr);
    }

    if (displayName.trim()) return displayName.trim();

    const sharedSnap = await getDocs(
      query(collection(db, 'sharedArtworks'), where('uid', '==', uid), limit(8))
    );
    for (const sharedDoc of sharedSnap.docs) {
      const studentName = sharedDoc.data()?.studentName;
      if (typeof studentName === 'string' && studentName.trim() && !isGenericArtistLabel(studentName)) {
        return studentName.trim();
      }
    }

    const userSnap = await getDoc(doc(db, 'users', uid));
    if (userSnap.exists()) {
      const userData = userSnap.data();
      const fromUser = userData.displayName || userData.name || userData.studentName || '';
      if (fromUser.trim()) return fromUser.trim();
      const email = userData.email || '';
      if (email.includes('@')) return email.split('@')[0].trim();
    }

    return profile?.nickname || profile?.name || '';
  } catch (err) {
    console.warn('[museumActivityInsights] name resolve failed:', uid, err);
    return '';
  }
}

async function resolvePeerArtworkArtistName(artworkId) {
  if (!artworkId) return '';
  try {
    const sharedSnap = await getDocs(
      query(collection(db, 'sharedArtworks'), where('artworkId', '==', artworkId), limit(5))
    );
    for (const sharedDoc of sharedSnap.docs) {
      const name = pickPeerArtistName(sharedDoc.data());
      if (name && !isGenericArtistLabel(name)) return name;
    }
  } catch (err) {
    console.warn('[museumActivityInsights] sharedArtworks name lookup failed:', artworkId, err);
  }
  return '';
}

/** Resolve a single peer label (exported for story viewer fallback). */
export async function resolveMuseumPeerDisplayName(peer = {}) {
  const inline =
    peer.userName && !isGenericArtistLabel(peer.userName) ? String(peer.userName).trim() : '';
  if (inline) return inline;

  const fromWall = await resolvePeerArtworkArtistName(peer.id);
  if (fromWall) return fromWall;

  const fromProfile = await resolveArtistDisplayName(peer.uid || peer.userId);
  if (fromProfile && !isGenericArtistLabel(fromProfile)) return fromProfile;

  return '';
}

async function enrichPeersWithDisplayNames(peers = []) {
  return Promise.all(
    peers.map(async (peer) => {
      const userName = await resolveMuseumPeerDisplayName(peer);
      return { ...peer, userName };
    })
  );
}

const previewUrl = (item = {}) => {
  const raw =
    item.thumbnailUrl ||
    item.imageUrl ||
    item.artworkUrl ||
    item.transformedImageUrl ||
    item.artworkImageUrl ||
    item.artworkMeta?.imageUrl ||
    '';
  return typeof raw === 'string' ? raw : '';
};

function normalizeShareDoc(doc, source) {
  const data = doc.data();
  return {
    id: doc.id,
    source,
    ...data,
  };
}

function getActivityDescriptor(item) {
  if (!item) return null;

  if (item.source === 'book' && item.book && item.activityCode) {
    return {
      type: 'book',
      key: `book:${item.book}:${item.activityCode}`,
      book: item.book,
      activityCode: item.activityCode,
      title: `${item.book} · Activity ${item.activityCode}`,
      coverUrl: previewUrl(item),
    };
  }

  if (item.source === 'activity' && item.activityId) {
    return {
      type: 'activity',
      key: `activity:${item.activityId}`,
      activityId: String(item.activityId),
      title: item.title || item.activityTitle || `Daily activity`,
      coverUrl: previewUrl(item),
    };
  }

  if (item.source === 'ways-of-seeing') {
    const styleId =
      item.styleId ||
      (item.styleName || '').toLowerCase().replace(/\s+/g, '') ||
      null;
    if (!styleId) return null;
    return {
      type: 'ways',
      key: `ways:${styleId}`,
      styleId,
      title: item.styleName || item.title || 'Ways of Seeing',
      coverUrl: previewUrl(item),
    };
  }

  if (item.source === 'art-history') {
    const flow = item.flowActivityMeta || null;
    const meta = item.artworkMeta || {};
    const typeKey = flow?.artworkTypeKey || meta?.artworkTypeKey;
    if (typeKey) {
      const level = flow?.level ?? meta?.level ?? '';
      return {
        type: 'art-history-flow',
        key: `ah:flow:${typeKey}:L${level}`,
        typeKey,
        level: String(level),
        title: flow?.title || meta?.title || 'Art History activity',
        coverUrl: previewUrl(item) || previewUrl(meta),
      };
    }
    const artworkId = item.artworkId || meta?.id;
    if (artworkId) {
      return {
        type: 'art-history-artwork',
        key: `ah:artwork:${artworkId}`,
        artworkId,
        title: meta?.title || item.title || 'Art History artwork',
        coverUrl: previewUrl(item) || previewUrl(meta),
      };
    }
  }

  return null;
}

function normalizePeer(doc, source, descriptor) {
  const data = doc.data();
  const uid = data.uid || data.userId;
  const inlineName = pickPeerArtistName(data);
  return {
    id: doc.id,
    source,
    uid,
    userName: inlineName || null,
    title: pickPeerArtworkTitle(data, descriptor),
    imageUrl: previewUrl(data),
    createdAt: data.createdAt,
  };
}

async function fetchPeers(descriptor, currentUid) {
  if (!descriptor) return [];

  let docs = [];

  try {
    switch (descriptor.type) {
      case 'book': {
        const snap = await getDocs(
          query(
            collection(db, 'bookArtworks'),
            where('book', '==', descriptor.book),
            where('activityCode', '==', descriptor.activityCode),
            where('visibility', '==', 'public')
          )
        );
        docs = snap.docs.filter((d) => {
          const data = d.data();
          return !data.isSystemSeed && data.uid !== 'system-book-seed';
        });
        break;
      }
      case 'activity': {
        const snap = await getDocs(
          query(
            collection(db, 'submissions'),
            where('activityId', '==', descriptor.activityId),
            where('isPublic', '==', true),
            limit(PAGING.PUBLIC_SUBMISSIONS_PER_ACTIVITY_LIMIT)
          )
        );
        docs = snap.docs;
        break;
      }
      case 'ways': {
        const snap = await getDocs(
          query(
            collection(db, 'tutorialArtworks'),
            where('styleId', '==', descriptor.styleId),
            where('visibility', '==', 'public')
          )
        );
        docs = snap.docs;
        break;
      }
      case 'art-history-artwork': {
        const snap = await getDocs(
          query(
            collection(db, 'artHistorySharedArtworks'),
            where('artworkId', '==', descriptor.artworkId),
            where('visibility', '==', 'public')
          )
        );
        docs = snap.docs;
        break;
      }
      case 'art-history-flow': {
        const snap = await getDocs(
          query(
            collection(db, 'artHistorySharedArtworks'),
            where('visibility', '==', 'public'),
            limit(120)
          )
        );
        docs = snap.docs.filter((d) => {
          const data = d.data();
          const flow = data.flowActivityMeta || {};
          const meta = data.artworkMeta || {};
          const typeKey = flow.artworkTypeKey || meta.artworkTypeKey;
          const level = String(flow.level ?? meta.level ?? '');
          return typeKey === descriptor.typeKey && level === descriptor.level;
        });
        break;
      }
      default:
        break;
    }
  } catch (err) {
    console.warn('[museumActivityInsights] peer fetch failed:', descriptor?.key, err);
    return [];
  }

  return docs
    .map((d) => normalizePeer(d, descriptor.type.startsWith('art-history') ? 'art-history' : descriptor.type, descriptor))
    .filter((p) => p.uid && p.uid !== currentUid && p.imageUrl);
}

async function fetchUserPublicShares(uid) {
  const [artHistorySnap, bookSnap, waysSnap, submissionsSnap] = await Promise.all([
    getDocs(
      query(
        collection(db, 'artHistorySharedArtworks'),
        where('uid', '==', uid),
        where('visibility', '==', 'public')
      )
    ),
    getDocs(
      query(
        collection(db, 'bookArtworks'),
        where('uid', '==', uid),
        where('visibility', '==', 'public')
      )
    ),
    getDocs(
      query(
        collection(db, 'tutorialArtworks'),
        where('userId', '==', uid),
        where('visibility', '==', 'public')
      )
    ),
    getDocs(
      query(
        collection(db, 'submissions'),
        where('uid', '==', uid),
        where('isPublic', '==', true)
      )
    ),
  ]);

  const bookItems = bookSnap.docs
    .map((d) => normalizeShareDoc(d, 'book'))
    .filter((item) => item.uid !== 'system-book-seed' && !item.isSystemSeed);

  return [
    ...artHistorySnap.docs.map((d) => normalizeShareDoc(d, 'art-history')),
    ...bookItems,
    ...waysSnap.docs.map((d) => normalizeShareDoc(d, 'ways-of-seeing')),
    ...submissionsSnap.docs.map((d) => normalizeShareDoc(d, 'activity')),
  ];
}

/**
 * Story groups for activities the user joined in the Artist Salon where others also contributed.
 */
export async function fetchMuseumActivityStoryGroups(uid) {
  if (!uid) return [];

  const shares = await fetchUserPublicShares(uid);
  const byKey = new Map();

  shares.forEach((share) => {
    const descriptor = getActivityDescriptor(share);
    if (!descriptor) return;
    if (!byKey.has(descriptor.key)) {
      byKey.set(descriptor.key, { descriptor, userShare: share });
    }
  });

  const stories = await Promise.all(
    Array.from(byKey.values()).map(async ({ descriptor, userShare }) => {
      const peers = await fetchPeers(descriptor, uid);
      if (peers.length === 0) return null;

      const sortedPeers = [...peers].sort((a, b) => {
        const ta = a.createdAt?.toMillis?.() ?? a.createdAt?._seconds * 1000 ?? 0;
        const tb = b.createdAt?.toMillis?.() ?? b.createdAt?._seconds * 1000 ?? 0;
        return tb - ta;
      });
      const peersWithNames = await enrichPeersWithDisplayNames(sortedPeers);

      return {
        id: descriptor.key,
        activityKey: descriptor.key,
        title: descriptor.title,
        coverUrl: descriptor.coverUrl || peersWithNames[0]?.imageUrl || '',
        peerCount: peersWithNames.length,
        peers: peersWithNames,
        userShareId: userShare.id,
        source: userShare.source,
        galleryTab:
          userShare.source === 'book'
            ? 'books'
            : userShare.source === 'ways-of-seeing'
              ? 'ways'
              : userShare.source === 'activity'
                ? 'activities'
                : 'art-history',
      };
    })
  );

  return stories.filter(Boolean);
}
